import Skeleton from '@/components/common/Skeleton';
import useBannerQuery from '@/hooks/RQHooks/layout/useBannerQuery';
import { GA_EVENT } from '@/lib/gtag';
import { useViewportStore } from '@/store/useViewportStore';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const MOBILE_BANNER_RATIO = '2.2 / 1';
const DESKTOP_DOUBLE_RATIO = '2.55 / 1';
const DESKTOP_SINGLE_RATIO = '4.6 / 1';

const HomeBannerCarousel = ({ type }: { type: 'market' | 'experience' }) => {
  const isMobile = useViewportStore((store) => store.isMobile);
  const { data, isPending } = useBannerQuery({
    bannerType: 'MAIN',
    exposePosition: 'HOME',
    detailPlatformType: type === 'market' ? 'MARKET' : 'TOUR',
  });

  const hasMultipleSlides = (data?.length ?? 0) > 1;
  const isDesktopSingleSlide = !isMobile && (data?.length ?? 0) === 1;
  const showDoubleSlide = !isMobile && (hasMultipleSlides || isDesktopSingleSlide);
  const showDesktopDots = !isMobile && Boolean(data?.length);
  const carouselItems = data?.map((item) => ({ key: String(item.id), item })) ?? [];

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      slidesToScroll: 1,
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  const [curSlideIndex, setCurSlideIndex] = useState(0);
  const [singleDragOffset, setSingleDragOffset] = useState(0);
  const [isSingleDragging, setIsSingleDragging] = useState(false);
  const singleDragStartXRef = useRef<number | null>(null);
  const singleDragMovedRef = useRef(false);
  const suppressSingleClickRef = useRef(false);

  const handleBannerClick = (
    banner?: { id: number; mainTitle: string; linkUrl: string },
    index: number = 0
  ) => {
    if (suppressSingleClickRef.current) return;
    if (!banner?.linkUrl) return;

    GA_EVENT('select_promotion', {
      promotion_id: String(banner.id),
      promotion_name: banner.mainTitle || `home_banner_${index + 1}`,
      location_id: `HOME_${type.toUpperCase()}_MAIN_BANNER_${index + 1}`,
    });

    window.location.href = banner.linkUrl;
  };

  const handleDotClick = (index: number) => {
    emblaApi?.scrollTo(index);
  };

  const isDotActive = (index: number) => {
    if (!data?.length) return false;
    if (isDesktopSingleSlide) return true;
    if (!showDoubleSlide) return curSlideIndex === index;

    const nextVisibleIndex = (curSlideIndex + 1) % data.length;
    return index === curSlideIndex || index === nextVisibleIndex;
  };

  const handleSinglePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDesktopSingleSlide) return;

    singleDragStartXRef.current = e.clientX;
    singleDragMovedRef.current = false;
  };

  const handleSinglePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDesktopSingleSlide || singleDragStartXRef.current === null) return;

    const nextOffset = Math.max(-32, Math.min(32, e.clientX - singleDragStartXRef.current));

    if (Math.abs(nextOffset) > 4 && !singleDragMovedRef.current) {
      singleDragMovedRef.current = true;
      setIsSingleDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    if (singleDragMovedRef.current) {
      setSingleDragOffset(nextOffset);
    }
  };

  const handleSinglePointerEnd = () => {
    if (!isDesktopSingleSlide) return;

    if (singleDragMovedRef.current) {
      suppressSingleClickRef.current = true;
      window.setTimeout(() => {
        suppressSingleClickRef.current = false;
      }, 0);
    }

    singleDragStartXRef.current = null;
    setIsSingleDragging(false);
    setSingleDragOffset(0);
  };

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurSlideIndex(emblaApi.selectedScrollSnap());
    };

    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    setCurSlideIndex(0);
    emblaApi?.scrollTo(0);
  }, [type, emblaApi]);

  if (data && data.length <= 0) {
    return null;
  }

  if (isDesktopSingleSlide && data?.[0]) {
    return (
      <CarouselRoot $isMobile={isMobile}>
        <SingleBannerViewport
          onPointerDown={handleSinglePointerDown}
          onPointerMove={handleSinglePointerMove}
          onPointerUp={handleSinglePointerEnd}
          onPointerCancel={handleSinglePointerEnd}
          onPointerLeave={handleSinglePointerEnd}>
          <SingleBannerTrack $offset={singleDragOffset} $isDragging={isSingleDragging}>
            <EmblaSlide $isMobile={isMobile} $showDoubleSlide={true}>
              <BannerFrame $isMobile={isMobile} $showDoubleSlide={true}>
                <BannerButton type="button" onClick={() => handleBannerClick(data[0], 0)}>
                  <BannerImage src={data[0].imageUrl} alt={data[0].mainTitle} />
                </BannerButton>
              </BannerFrame>
            </EmblaSlide>
            <EmblaSlide $isMobile={isMobile} $showDoubleSlide={true}>
              <EmptyBannerFrame />
            </EmblaSlide>
          </SingleBannerTrack>
        </SingleBannerViewport>

        <DotsWrapper>
          <Dot type="button" $isActive={true} onClick={() => handleDotClick(0)} />
        </DotsWrapper>
      </CarouselRoot>
    );
  }

  return (
    <CarouselRoot $isMobile={isMobile}>
      <EmblaCarouselOutWrapper ref={emblaRef}>
        <EmblaCarouselContainer $isMobile={isMobile}>
          {isPending ? (
            <EmblaSlide $isMobile={isMobile} $showDoubleSlide={showDoubleSlide}>
              <BannerFrame $isMobile={isMobile} $showDoubleSlide={showDoubleSlide}>
                <Skeleton width="100%" height="100%" />
              </BannerFrame>
            </EmblaSlide>
          ) : (
            carouselItems.map(({ key, item }, index) => (
              <EmblaSlide key={key} $isMobile={isMobile} $showDoubleSlide={showDoubleSlide}>
                {item ? (
                  <BannerFrame $isMobile={isMobile} $showDoubleSlide={showDoubleSlide}>
                    <BannerButton type="button" onClick={() => handleBannerClick(item, index)}>
                      <BannerImage src={item.imageUrl} alt={item.mainTitle} />
                    </BannerButton>
                  </BannerFrame>
                ) : (
                  <EmptyBannerFrame />
                )}
              </EmblaSlide>
            ))
          )}
        </EmblaCarouselContainer>

        {isMobile && hasMultipleSlides && (
          <EmblaCarouselController>
            <CarouselIndexViewer>
              {curSlideIndex + 1}&nbsp;&nbsp;/&nbsp;&nbsp;{data?.length}
            </CarouselIndexViewer>
          </EmblaCarouselController>
        )}
      </EmblaCarouselOutWrapper>

      {showDesktopDots && (
        <DotsWrapper>
          {data?.map((item, index) => (
            <Dot
              key={item.id}
              type="button"
              $isActive={isDotActive(index)}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </DotsWrapper>
      )}
    </CarouselRoot>
  );
};

export default HomeBannerCarousel;

const CarouselRoot = styled.div<{ $isMobile: boolean }>`
  position: relative;
  background: ${({ theme }) => theme.colors.white};
  padding: 0;
`;

const EmblaCarouselOutWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const SingleBannerViewport = styled.div`
  overflow: hidden;
  touch-action: pan-y;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const SingleBannerTrack = styled.div<{ $offset: number; $isDragging: boolean }>`
  display: flex;
  width: 100%;
  gap: 8px;
  transform: translateX(${({ $offset }) => `${$offset}px`});
  transition: ${({ $isDragging }) => ($isDragging ? 'none' : 'transform 0.25s ease-out')};
  will-change: transform;
`;

const EmblaCarouselContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  width: 100%;
  gap: ${({ $isMobile }) => ($isMobile ? '0' : '8px')};
`;

const EmblaSlide = styled.div<{ $isMobile: boolean; $showDoubleSlide: boolean }>`
  flex: 0 0
    ${({ $isMobile, $showDoubleSlide }) =>
      $isMobile || !$showDoubleSlide ? '100%' : 'calc((100% - 8px) / 2)'};
  min-width: 0;
`;

const BannerFrame = styled.div<{ $isMobile: boolean; $showDoubleSlide: boolean }>`
  width: 100%;
  overflow: hidden;
  border-radius: ${({ $isMobile }) => ($isMobile ? '12px' : '14px')};
  background-color: ${({ theme, $isMobile }) =>
    $isMobile ? theme.colors.gray50 : theme.colors.white};
  ${({ $isMobile, $showDoubleSlide }) =>
    $isMobile || $showDoubleSlide
      ? `aspect-ratio: ${$isMobile ? MOBILE_BANNER_RATIO : DESKTOP_DOUBLE_RATIO};`
      : `aspect-ratio: ${DESKTOP_SINGLE_RATIO};`}
`;

const EmptyBannerFrame = styled.div`
  width: 100%;
  aspect-ratio: ${DESKTOP_DOUBLE_RATIO};
  border-radius: 14px;
  background-color: ${({ theme }) => theme.colors.white};
`;

const BannerButton = styled.button`
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const BannerImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CarouselIndexViewer = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 50px;
  height: 20px;
  border-radius: 24px;
  background-color: rgba(255, 255, 255, 0.8);
  color: #222;
  font-size: 11px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 1;
  font-weight: 600;
  pointer-events: auto;
`;

const EmblaCarouselController = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const DotsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 20px;
`;

const Dot = styled.button<{ $isActive: boolean }>`
  width: 6px;
  height: 6px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.gray1000 : theme.colors.gray300};
  cursor: pointer;
  transition: background-color 0.2s ease;
`;
